import {
  Box,
  Button,
  Column,
  FormField,
  InputPasswordField,
  InputTextField,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { type KeyboardEvent } from 'react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AcceptTerms } from '~/components/authentication/components/AcceptTerms';
import { OfficeTnCTypeEnum } from '~/domain/Consents';
import { useSignUp } from '~/domain/SignUp';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { removeWhitespace } from '~/utils/String';
import { useTrackEvent } from '~/hooks/useTrackEvent';
import { AuthErrorBanner, useAuthFormSubmit, useCancelPartialSession } from '../shared';
import { FreeToJoinBanner } from './FreeToJoinBanner';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

// DS Typography has no cursor prop — required for interactive non-button text
const LINK_STYLE = { cursor: 'pointer' } as const;

const activateOnSpaceOrEnter = (handler: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

export interface ISignUpAccountFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

export const SignUpForm = () => {
  const methods = useForm<ISignUpAccountFormValues>({
    defaultValues: { email: '', password: '', confirmPassword: '', termsAccepted: false },
    mode: 'onSubmit',
  });

  return (
    <FormProvider {...methods}>
      <SignUpFormInner />
    </FormProvider>
  );
};

// Order for picking which specific validation message to surface in the banner.
const FIELD_ORDER: Array<keyof ISignUpAccountFormValues> = ['email', 'password', 'confirmPassword'];

const SignUpFormInner = () => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const { mutate: signUp, isPending } = useSignUp();
  const cancelPartialSession = useCancelPartialSession();

  const { submit, bannerVisible, bannerRef } = useAuthFormSubmit<ISignUpAccountFormValues>({
    onValid: ({ email, password }) => {
      trackEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_CLICKED);
      const referralId = localStorage.getItem('referralId') || undefined;
      signUp({ email, password, referralId }, { onSuccess: () => navigate('/signup/your-info') });
    },
  });

  const {
    register,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<ISignUpAccountFormValues>();

  const goToLogin = cancelPartialSession;

  const emailRegister = register('email', {
    required: 'Email is required',
    pattern: { value: EMAIL_REGEX, message: 'The email address entered is invalid.' },
  });

  // Surface every validation message (one per field) so the banner can stack
  // them. Required-empty errors stay suppressed — the empty-fields default
  // copy covers those.
  const specificErrorMessages = FIELD_ORDER.map((key) => errors[key])
    .filter((err): err is NonNullable<typeof err> => Boolean(err) && err?.type !== 'required')
    .map((err) => err.message as string | undefined)
    .filter((msg): msg is string => Boolean(msg));

  // When the user fails the "passwords match" check the error lives on
  // `confirmPassword`; the spec calls for highlighting both fields.
  const passwordMismatch = errors.confirmPassword?.type === 'validate';

  return (
    <form onSubmit={submit} data-testid="signup-form" noValidate>
      <Column pt={6} gap={8}>
        <Column gap={4}>
          <Typography bold size="heading_3" color={colors.primary[500]}>
            Welcome, Offices!
          </Typography>
          <Typography size="body_large_tight" color={colors.neutral[500]}>
            Create your account and join thousands of offices using GoTu to find trusted dental
            professionals quickly.
          </Typography>
        </Column>

        <FreeToJoinBanner />

        {/* Stable scrollIntoView target when the banner is hidden. */}
        <Box ref={bannerRef}>
          <AuthErrorBanner visible={bannerVisible} message={specificErrorMessages} />
          <Column gap={8} pt={bannerVisible ? 3 : 0}>
            <FormField label="Email">
              <InputTextField
                data-cy="input-email"
                placeholder="Enter your email"
                isError={Boolean(errors.email)}
                {...emailRegister}
                onChange={(e) => {
                  const stripped = removeWhitespace(e.target.value);
                  if (stripped !== e.target.value) e.target.value = stripped;
                  emailRegister.onChange(e);
                }}
              />
            </FormField>
            <FormField label="Password">
              <InputPasswordField
                data-cy="input-password"
                placeholder="Enter your password"
                isError={Boolean(errors.password) || passwordMismatch}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: MIN_PASSWORD_LENGTH,
                    message: `All passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`,
                  },
                })}
              />
            </FormField>
            <FormField label="Confirm Password">
              <InputPasswordField
                data-cy="input-confirm-password"
                placeholder="Confirm your password"
                isError={Boolean(errors.confirmPassword)}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === getValues('password') || 'Passwords do not match. Please try again.',
                })}
              />
            </FormField>
          </Column>
        </Box>

        {/* Reuses the legacy AcceptTerms control so the new signup gates the
            toggle through the same scroll-to-accept consent popup as the
            pre-redesign flow — without it, BE flips the user back into the
            "updated terms" prompt on dashboard after they finish signup. */}
        <Box>
          <Controller
            name="termsAccepted"
            control={control}
            rules={{ validate: (v) => v === true || 'You must accept the Terms and Conditions' }}
            render={({ field }) => (
              <AcceptTerms
                value={field.value}
                onChange={field.onChange}
                consent={OfficeTnCTypeEnum.OFFICE_TNC}
              />
            )}
          />
        </Box>

        <Row justify="center">
          <Button
            data-cy="btn-sign-up"
            type="submit"
            variant="primary"
            size="lg"
            isLoading={isPending}
          >
            Start Finding Coverage
          </Button>
        </Row>

        <Row justify="center" align="center" wrap="nowrap" gap={1}>
          <Typography size="body_large" color={colors.neutral[500]}>
            Already have an account?
          </Typography>
          <Typography
            as="span"
            bold
            size="body_large"
            color={colors.secondary[500]}
            style={LINK_STYLE}
            role="button"
            tabIndex={0}
            data-cy="link-log-in"
            onClick={goToLogin}
            onKeyDown={activateOnSpaceOrEnter(goToLogin)}
          >
            Log In
          </Typography>
        </Row>
      </Column>
    </form>
  );
};
